import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class CustomFormValidators {
  // Valida que dos campos sean iguales
  static matchWith(controlName: string, matchingControlName: string): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const control = formGroup.get(controlName);
      const matchingControl = formGroup.get(matchingControlName);
      if (!control || !matchingControl) return null;

      if (matchingControl.errors && !matchingControl.errors['mustMatch']) return null;

      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      } else {
        matchingControl.setErrors(null);
      }

      return null;
    };
  }

  // Valida email con TLD obligatorio
  static emailWithTld(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) return null; // no valida vacío (usa Validators.required aparte)

      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

      return emailRegex.test(value) ? null : { invalidEmail: true };
    };
  }

  // Valida que dos campos NO sean iguales
  static fieldsNotEqual(field1: string, field2: string): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const f1 = formGroup.get(field1);
      const f2 = formGroup.get(field2);
      if (!f1 || !f2) return null;

      return f1.value !== f2.value ? null : { fieldsNotEqual: true };
    };
  }

  // Fuerza de contraseña: debe tener mayúscula, minúscula, número y carácter especial
  static passwordStrength(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value || '';
      const hasUpper = /[A-Z]/.test(value);
      const hasLower = /[a-z]/.test(value);
      const hasNumber = /\d/.test(value);
      const hasSpecial = /[\W_]/.test(value);
      return hasUpper && hasLower && hasNumber && hasSpecial ? null : { passwordStrength: true };
    };
  }

  // Valida CURP mexicana
  static curpValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const curp = control.value?.toUpperCase();
      if (!curp) return null;
      const regex = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[0-9A-Z]\d$/;
      return regex.test(curp) ? null : { invalidCurp: true };
    };
  }

  // Valida RFC mexicano
  static rfcValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rfc = control.value?.toUpperCase();
      if (!rfc) return null;
      const regex = /^([A-ZÑ&]{3,4})\d{6}[A-Z0-9]{3}$/;
      return regex.test(rfc) ? null : { invalidRfc: true };
    };
  }

  // Valida que la fecha no esté en el futuro
  static dateNotInFuture(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const inputDate = new Date(control.value);
      return isNaN(inputDate.getTime()) || inputDate <= new Date() ? null : { futureDate: true };
    };
  }

  // Valida que la fecha no sea anterior a cierto mínimo
  static dateNotBefore(minDate: string | Date): ValidatorFn {
    const min = new Date(minDate);
    return (control: AbstractControl): ValidationErrors | null => {
      const inputDate = new Date(control.value);
      return isNaN(inputDate.getTime()) || inputDate >= min
        ? null
        : { tooEarlyDate: { minDate: min.toISOString().split('T')[0] } };
    };
  }

  // Valida que la fecha esté dentro de un rango
  static dateWithinRange(minDate: string | Date, maxDate: string | Date): ValidatorFn {
    const min = new Date(minDate);
    const max = new Date(maxDate);
    return (control: AbstractControl): ValidationErrors | null => {
      const inputDate = new Date(control.value);
      return isNaN(inputDate.getTime()) || (inputDate >= min && inputDate <= max)
        ? null
        : {
            dateOutOfRange: {
              min: min.toISOString().split('T')[0],
              max: max.toISOString().split('T')[0],
            },
          };
    };
  }
}
